import { motion } from 'framer-motion';
import { CheckCircle, Users, Target, Heart } from 'lucide-react';
import { contactInfo } from '../data/services';

export default function About() {
  const values = [
    {
      icon: CheckCircle,
      title: 'Quality Service',
      description: 'We deliver exceptional service quality with attention to every detail.',
    },
    {
      icon: Users,
      title: 'Expert Team',
      description: 'Our professionals are trained and experienced in their respective fields.',
    },
    {
      icon: Target,
      title: 'Customer Focus',
      description: 'Your satisfaction is our priority. We listen and deliver accordingly.',
    },
    {
      icon: Heart,
      title: 'Reliability',
      description: 'Count on us for timely service and consistent results every time.',
    },
  ];

  return (
    <main className="min-h-screen pt-20 bg-[#dbeafe]" data-testid="about-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-[#dbeafe] to-[#bfdbfe] py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <span className="inline-block text-blue-500 font-medium text-sm uppercase tracking-wider">
                About Us
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight font-['Oswald']">
                Your Trusted Partner for
                <span className="text-blue-600"> Home Services</span>
              </h1>
              <p className="text-slate-700 leading-relaxed">
                At Venkateswara Hospitality & Facilities Management services Private Limited (VHFMS), we bring reliable home services right to your doorstep. Our goal is to simplify your everyday life by providing affordable, high-quality solutions for all your household needs.
              </p>
              <p className="text-slate-700 leading-relaxed">
                From cleaning and maintenance to repairs and home assistance, our skilled professionals ensure every service is delivered with precision, care, and professionalism.
              </p>
              <p className="text-slate-700 leading-relaxed">
                At VHFMS, we believe in creating happy, stress-free homes. No more searching for multiple service providers everything you need is just a tap away with our service-on-wheels approach, we take care of it all while you sit back and relax. 
              </p>
              <p className="text-slate-700 leading-relaxed">
                Your home deserves the best and we’re here to deliver it with convenience, speed, and reliability.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://mobisoftinfotech.com/resources/wp-content/uploads/2018/08/Banner.png"
                  alt="Professional Team"
                  className="w-full h-[400px] object-cover"
                />
              </div>
              {/* Decorative Element */}
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-blue-500 rounded-2xl -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#0021aa]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { number: '1000+', label: 'Happy Customers' },
              { number: '9+', label: 'Services Offered' },
              { number: '5+', label: 'Years Experience' },
              { number: '4.7', label: 'Average Rating' },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-white"
              >
                <p className="text-4xl lg:text-5xl font-bold font-['Oswald']">{stat.number}</p>
                <p className="text-blue-100 mt-2">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block text-blue-500 font-medium text-sm uppercase tracking-wider mb-3">
              Our Values
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 font-['Oswald']">
              What Sets Us Apart
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg text-center hover:shadow-xl transition-shadow"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <value.icon size={28} className="text-blue-500" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-3 font-['Oswald']">{value.title}</h3>
                <p className="text-slate-600 text-sm">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-slate-900 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block text-blue-400 font-medium text-sm uppercase tracking-wider mb-3">Core Goal</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8 font-['Oswald']">Our Mission</h2>
            <div className="relative inline-block">
              <span className="absolute -top-6 -left-6 text-6xl text-slate-800 font-serif font-bold">"</span>
              <p className="text-xl sm:text-2xl text-slate-300 leading-relaxed italic relative z-10">
                To provide exceptional home services that exceed customer expectations,
                delivered by skilled professionals with integrity and a commitment to excellence.
                We aim to make every home in Machilipatnam a better place to live.
              </p>
              <span className="absolute -bottom-12 -right-6 text-6xl text-slate-800 font-serif font-bold">"</span>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
